import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ExternalLink, Images } from 'lucide-react';
import { getProjects } from '../api';
import useSeo from '../hooks/useSeo';
import { optimizeImageUrl } from '../lib/optimizeImage';

const CATEGORY_DESCRIPTIONS = {
  All: 'Selected motion design, character design, animation, video editing, and photography projects from Mansfield, TX — serving the Dallas–Fort Worth Metroplex.',
  'Motion Design': 'Motion graphics, animated logo sequences, and dynamic brand animations from Mansfield, TX — serving the DFW Metroplex for digital and broadcast.',
  'Character Design': 'Original character concepts, mascots, and personality-driven animation. Mansfield, TX-based, serving DFW and remote clients.',
  Animation: '2D and 3D animation for explainer videos, product launches, and educational content. Animator based in Mansfield, TX (DFW Metroplex).',
  'Video Editing': 'Story-driven video editing — corporate, promotional, social, and broadcast. DFW Metroplex video editor based in Mansfield, TX.',
  Photography: 'Family, real estate, event, corporate, and brand photography in Mansfield, TX — serving the Dallas–Fort Worth Metroplex.',
};

const Portfolio = () => {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useSeo({
    title: selectedCategory === 'All' ? 'Portfolio' : `${selectedCategory} Portfolio`,
    description: CATEGORY_DESCRIPTIONS[selectedCategory] || CATEGORY_DESCRIPTIONS.All,
  });

  const categories = ['All', 'Motion Design', 'Character Design', 'Animation', 'Video Editing', 'Photography'];

  const isGalleryProject = (project) =>
    project?.category === 'Photography' &&
    Array.isArray(project.gallery) &&
    project.gallery.length > 0;

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const data = await getProjects();
        setProjects(data);
      } catch (e) {
        console.error('Failed to load projects', e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filteredProjects =
    selectedCategory === 'All'
      ? projects
      : projects.filter((project) => project.category === selectedCategory);

  return (
    <div className="min-h-screen bg-black text-white pt-16">
      {/* Hero */}
      <section className="py-14 lg:py-20 bg-gradient-to-b from-blue-900/20 to-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="page-title text-4xl lg:text-6xl font-bold mb-6">
            <span className="bg-gradient-to-r from-blue-400 via-blue-400 to-lime-400 bg-clip-text text-transparent">Portfolio</span>
          </h1>
          <p className="text-lg sm:text-xl text-gray-300 max-w-3xl mx-auto">
            A showcase of motion design, animation, video editing, and photography projects for leading brands.
          </p>
        </div>
      </section>

      {/* Filter */}
      <section className="py-8 bg-black border-b border-gray-800 sticky top-16 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-3 justify-center" data-testid="portfolio-filters">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                data-testid={`filter-${category.toLowerCase().replace(/\s+/g, '-')}`}
                className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${
                  selectedCategory === category
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <p className="text-center text-gray-400" data-testid="portfolio-loading">Loading projects...</p>
          ) : filteredProjects.length === 0 ? (
            <p className="text-center text-gray-400" data-testid="portfolio-empty">No projects in this category yet.</p>
          ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="portfolio-grid">
            {filteredProjects.map((project) => (
              <Link
                key={project.id}
                to={`/portfolio/${project.slug || project.id}`}
                data-testid={`project-card-${project.id}`}
                className="block"
              >
                <Card
                  className="bg-gray-800/50 border-gray-700 hover:border-blue-400 transition-all duration-300 overflow-hidden group cursor-pointer h-full"
                >
                <div className="relative aspect-video overflow-hidden">
                  <img
                    src={optimizeImageUrl(project.imageUrl, 800)}
                    alt={project.title}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent opacity-60 group-hover:opacity-40 transition-opacity"></div>
                  {isGalleryProject(project) && (
                    <div
                      className="absolute top-3 right-3 z-10 flex items-center gap-1.5 bg-black/70 backdrop-blur text-white text-xs px-2.5 py-1 rounded-full border border-white/10"
                      data-testid={`gallery-badge-${project.id}`}
                    >
                      <Images size={12} />
                      <span>{project.gallery.length + (project.gallery.includes(project.imageUrl) ? 0 : 1)} photos</span>
                    </div>
                  )}
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="w-16 h-16 rounded-full bg-blue-600 flex items-center justify-center">
                      {isGalleryProject(project) ? (
                        <Images size={24} className="text-white" />
                      ) : (
                        <ExternalLink size={24} className="text-white" />
                      )}
                    </div>
                  </div>
                </div>
                <CardContent className="p-6">
                  <Badge className="mb-3 bg-blue-600/20 text-blue-400 border border-blue-400/30 hover:bg-blue-600/30">
                    {project.category}
                  </Badge>
                  <h3 className="text-xl font-semibold mb-2 text-white group-hover:text-blue-400 transition-colors">
                    {project.title}
                  </h3>
                  <p className="text-gray-400 text-sm mb-4">{project.description}</p>
                  <div className="flex flex-wrap gap-2">
                    {project.tags.map((tag, index) => (
                      <span
                        key={index}
                        className="text-xs px-3 py-1 bg-gray-700 text-gray-300 rounded-full"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </CardContent>
              </Card>
              </Link>
            ))}
          </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Portfolio;